import { useState, useEffect } from 'react';
import { CrossSvg } from '../../svg/Index';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { removeFromCart, optimisticRemoveItem, fetchCart } from '../slices/cartSlice';

const CartSidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    cartItems = [],
    subtotal = 0,
    removeLoading = false
  } = useSelector((state) => state?.cart || {});

  const [removeSuccess, setRemoveSuccess] = useState({});

  useEffect(() => {
    const offcanvasElement = document.getElementById('offcanvasExample');

    const handleShown = () => {
      dispatch(fetchCart());
    };

    if (offcanvasElement) {
      offcanvasElement.addEventListener('shown.bs.offcanvas', handleShown);

      return () => {
        offcanvasElement.removeEventListener('shown.bs.offcanvas', handleShown);
      };
    }
  }, [dispatch]);

  const handleRemoveItem = async (itemId, e) => {
    e.preventDefault();
    e.stopPropagation();

    setRemoveSuccess(prev => ({ ...prev, [itemId]: 'removing' }));

    dispatch(optimisticRemoveItem(itemId));

    try {
      await dispatch(removeFromCart(itemId)).unwrap();
      setRemoveSuccess(prev => ({ ...prev, [itemId]: 'success' }));

      setTimeout(() => {
        setRemoveSuccess(prev => ({ ...prev, [itemId]: null }));
      }, 2000);
    } catch (error) {
      console.error("Failed to remove item:", error);
      setRemoveSuccess(prev => ({ ...prev, [itemId]: 'error' }));

      dispatch(fetchCart());

      setTimeout(() => {
        setRemoveSuccess(prev => ({ ...prev, [itemId]: null }));
      }, 3000);
    }
  };

  const handleViewCart = () => {

    const modalElement = document.getElementById('offcanvasExample');
    const closeButton = modalElement.querySelector('[data-bs-dismiss="offcanvas"]');
    if (closeButton) {
      closeButton.click();
    }
    navigate("/view-cart");
  };

  const handleCheckout = () => {

    const modalElement = document.getElementById('offcanvasExample');
    const closeButton = modalElement.querySelector('[data-bs-dismiss="offcanvas"]');
    if (closeButton) {
      closeButton.click();
    }

    navigate("/checkout");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div
      className="offcanvas offcanvas-end cart-sidbar"
      tabIndex={-1}
      id="offcanvasExample"
      aria-labelledby="offcanvasExampleLabel"
    >
      <div className="offcanvas-header d-flex align-items-center justify-content-between border-bottom">
        <h5 className="offcanvas-title font--18 fw-medium" id="offcanvasExampleLabel">
          Shopping cart
        </h5>
        <button
          type="button"
          className="close-btn"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        >
          Close <CrossSvg />
        </button>
      </div>
      <div className="offcanvas-body mt-2">
        {cartItems.length == 0 ? (
          <div className="text-center py-5">
            <div className="mb-4">
              <img
                src="/image/empty-cart.svg"
                alt="Empty Cart"
                style={{ maxWidth: '150px', opacity: 0.7 }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <p className="text-muted mb-3">Your cart is empty</p>
       
          </div>
        ) : (
          <>
            {cartItems.map((item) => (
              <div
                key={item.id}
                className={`cart-item d-flex align-items-start gap-2 justify-content-between mb-4 position-relative ${removeSuccess[item.id] == 'removing' ? 'opacity-50' : ''
                  }`}
              >
                <div className='cart-left d-flex align-items-center gap-3'>
                  <div className="sm-item">
                    <img
                      className="img-fluid"
                      alt={item?.name || "product"}
                      src={item?.image || "/image/item-1.png"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/image/item-1.png";
                      }}
                    />
                  </div>

                  <div className='card-content'>
                    <h3 className='mb-2 font--15 fw-medium'>{item.name}</h3>
                    <p className='mb-2'>
                      Qty: <span className='text-dark'>{item.quantity}</span>
                      {item.size && ` | Size: `}<span className='text-dark'>{item.size || ''}</span>
                      {item.variant_title && (
                        <> | Variant: <span className='text-dark'>{item.variant_title}</span></>
                      )}
                      {item.brand && (
                        <> | Brand: <span className='text-dark'>{item.brand}</span></>
                      )}
                    </p>
                    <h3 className='font--18'>{formatCurrency(item.price * item.quantity)}</h3>
                    {item.quantity > 1 && (
                      <small className="text-muted">
                        ({formatCurrency(item.price)} each)
                      </small>
                    )}
                  </div>
                </div>
                <button
                  className='cancel-card bg-transparent border-0 p-0'
                  onClick={(e) => handleRemoveItem(item.id, e)}
                  disabled={removeLoading || removeSuccess[item.id] == 'removing'}
                  style={{ cursor: 'pointer', opacity: removeLoading ? 0.5 : 1 }}
                >
                  <img src="/image/close.svg" alt="Remove" />
                </button>

                {removeSuccess[item.id] == 'success' && (
                  <div className="position-absolute bottom-0 start-0 w-100 text-center text-success small">
                    Item removed
                  </div>
                )}
                {removeSuccess[item.id] == 'error' && (
                  <div className="position-absolute bottom-0 start-0 w-100 text-center text-danger small">
                    Failed to remove
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {cartItems.length > 0 && (
        <div className='offcanvas-footer white-card-footer p-3 border-top'>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <h5 className="mb-0 font--18 text-grey">Subtotal </h5>
            <h3 className='mb-0 link-text'>{formatCurrency(subtotal)}</h3>
          </div>

          {subtotal > 0 && (
            <p className="text-muted small mb-3">
              *Shipping and taxes calculated at checkout
            </p>
          )}

          <div className="mt-3 d-flex align-items-center gap-3">
            <button
              className="btn primary-btn flex-fill"
              onClick={handleViewCart}
              disabled={cartItems.length == 0}
            >
              <span className="me-2">
                <img src="/image/bag.svg" alt="" />
              </span>
              View Cart
            </button>
            <button
              className="btn secondary-btn flex-fill"
              onClick={handleCheckout}
              disabled={cartItems.length == 0}
            >
              <span className="me-2">
                <img src="/image/Category.svg" alt="" />
              </span>
              Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartSidebar;